import React from 'react';

// Enums
export enum PerformanceReviewType {
  STUDENTS = 'students',
  STAFF = 'staff',
  EXTERNAL = 'external',
}

export enum ReviewStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  OVERDUE = 'overdue',
}

export enum PerformanceRating {
  EXCELLENT = 'excellent',
  GOOD = 'good',
  SATISFACTORY = 'satisfactory',
  NEEDS_IMPROVEMENT = 'needs_improvement',
  UNSATISFACTORY = 'unsatisfactory',
}

export enum StudentGrade {
  GRADE_1 = '1',
  GRADE_2 = '2',
  GRADE_3 = '3',
  GRADE_4 = '4',
  GRADE_5 = '5',
  GRADE_6 = '6',
  GRADE_7 = '7',
  GRADE_8 = '8',
  GRADE_9 = '9',
  GRADE_10 = '10',
  GRADE_11 = '11',
  GRADE_12 = '12',
}

export enum StaffDepartment {
  TEACHING = 'teaching',
  ADMINISTRATION = 'administration',
  SUPPORT = 'support',
  MANAGEMENT = 'management',
}

export enum ExternalPartnerType {
  VENDOR = 'vendor',
  CONTRACTOR = 'contractor',
  CONSULTANT = 'consultant',
  SERVICE_PROVIDER = 'service_provider',
}

// Base Performance Review Interface
export interface BasePerformanceReview {
  id: string;
  name: string;
  reviewPeriod: string;
  reviewDate: string;
  reviewer: string;
  status: ReviewStatus;
  overallRating: PerformanceRating;
  overallScore: number; // 0-100
  comments: string;
  strengths: string[];
  areasForImprovement: string[];
  goals: string[];
  nextReviewDate: string;
}

// Student Performance Review
export interface StudentPerformanceReview extends BasePerformanceReview {
  studentId: string;
  grade: StudentGrade;
  section: string;
  academicPerformance: {
    gpa: number;
    attendance: number;
    subjectScores: SubjectScore[];
  };
  behavioralAssessment: {
    discipline: PerformanceRating;
    participation: PerformanceRating;
    teamwork: PerformanceRating;
    leadership: PerformanceRating;
  };
  extracurricularActivities: string[];
  parentFeedback?: string;
}

export interface SubjectScore {
  subject: string;
  score: number;
  grade: string;
  remarks: string;
}

// Staff Performance Review
export interface StaffPerformanceReview extends BasePerformanceReview {
  staffId: string;
  employeeId: string;
  department: StaffDepartment;
  position: string;
  joiningDate: string;
  supervisor: string;
  kpis: KPIAssessment[];
  competencies: CompetencyAssessment[];
  professionalDevelopment: {
    trainingCompleted: string[];
    certifications: string[];
    skillsAcquired: string[];
  };
  careerGoals: string[];
  promotionRecommendation: boolean;
  salaryReviewRecommendation: boolean;
}

export interface KPIAssessment {
  metric: string;
  target: number;
  achieved: number;
  percentage: number;
  rating: PerformanceRating;
}

export interface CompetencyAssessment {
  competency: string;
  rating: PerformanceRating;
  description: string;
}

// External Partner Performance Review
export interface ExternalPerformanceReview extends BasePerformanceReview {
  partnerId: string;
  partnerType: ExternalPartnerType;
  contractNumber: string;
  serviceDomain: string;
  contractStartDate: string;
  contractEndDate: string;
  serviceMetrics: ServiceMetric[];
  complianceAssessment: {
    regulatory: PerformanceRating;
    safety: PerformanceRating;
    quality: PerformanceRating;
    timeliness: PerformanceRating;
  };
  contractRenewalRecommendation: boolean;
  costEffectiveness: PerformanceRating;
}

export interface ServiceMetric {
  metric: string;
  target: string;
  actual: string;
  status: 'met' | 'exceeded' | 'below';
}

// Performance Summary Statistics
export interface PerformanceSummary {
  totalReviews: number;
  completedReviews: number;
  pendingReviews: number;
  overdueReviews: number;
  averageRating: number;
  ratingDistribution: {
    excellent: number;
    good: number;
    satisfactory: number;
    needsImprovement: number;
    unsatisfactory: number;
  };
}

// Filter and Search Interfaces
export interface PerformanceFilters {
  reviewType: PerformanceReviewType;
  status: ReviewStatus | 'all';
  rating: PerformanceRating | 'all';
  reviewPeriod: string | 'all';
  department?: StaffDepartment | 'all';
  grade?: StudentGrade | 'all';
  partnerType?: ExternalPartnerType | 'all';
  searchQuery: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

// Context State Interface
export interface PerformanceState {
  // Current view type
  currentReviewType: PerformanceReviewType;

  // Data
  studentReviews: StudentPerformanceReview[];
  staffReviews: StaffPerformanceReview[];
  externalReviews: ExternalPerformanceReview[];

  // Summary statistics
  studentSummary: PerformanceSummary;
  staffSummary: PerformanceSummary;
  externalSummary: PerformanceSummary;

  // UI State
  filters: PerformanceFilters;
  isLoading: boolean;
  error: string | null;
  selectedReview: BasePerformanceReview | null;
}

// Action Types
export enum PerformanceActionType {
  SET_REVIEW_TYPE = 'SET_REVIEW_TYPE',
  SET_STUDENT_REVIEWS = 'SET_STUDENT_REVIEWS',
  SET_STAFF_REVIEWS = 'SET_STAFF_REVIEWS',
  SET_EXTERNAL_REVIEWS = 'SET_EXTERNAL_REVIEWS',
  SET_FILTERS = 'SET_FILTERS',
  SET_SEARCH_QUERY = 'SET_SEARCH_QUERY',
  SET_LOADING = 'SET_LOADING',
  SET_ERROR = 'SET_ERROR',
  SET_SELECTED_REVIEW = 'SET_SELECTED_REVIEW',
  UPDATE_STUDENT_SUMMARY = 'UPDATE_STUDENT_SUMMARY',
  UPDATE_STAFF_SUMMARY = 'UPDATE_STAFF_SUMMARY',
  UPDATE_EXTERNAL_SUMMARY = 'UPDATE_EXTERNAL_SUMMARY',
  RESET_FILTERS = 'RESET_FILTERS',
}

// Action Interfaces
export interface SetReviewTypeAction {
  type: PerformanceActionType.SET_REVIEW_TYPE;
  payload: PerformanceReviewType;
}

export interface SetStudentReviewsAction {
  type: PerformanceActionType.SET_STUDENT_REVIEWS;
  payload: StudentPerformanceReview[];
}

export interface SetStaffReviewsAction {
  type: PerformanceActionType.SET_STAFF_REVIEWS;
  payload: StaffPerformanceReview[];
}

export interface SetExternalReviewsAction {
  type: PerformanceActionType.SET_EXTERNAL_REVIEWS;
  payload: ExternalPerformanceReview[];
}

export interface SetFiltersAction {
  type: PerformanceActionType.SET_FILTERS;
  payload: Partial<PerformanceFilters>;
}

export interface SetSearchQueryAction {
  type: PerformanceActionType.SET_SEARCH_QUERY;
  payload: string;
}

export interface SetLoadingAction {
  type: PerformanceActionType.SET_LOADING;
  payload: boolean;
}

export interface SetErrorAction {
  type: PerformanceActionType.SET_ERROR;
  payload: string | null;
}

export interface SetSelectedReviewAction {
  type: PerformanceActionType.SET_SELECTED_REVIEW;
  payload: BasePerformanceReview | null;
}

export interface UpdateStudentSummaryAction {
  type: PerformanceActionType.UPDATE_STUDENT_SUMMARY;
  payload: PerformanceSummary;
}

export interface UpdateStaffSummaryAction {
  type: PerformanceActionType.UPDATE_STAFF_SUMMARY;
  payload: PerformanceSummary;
}

export interface UpdateExternalSummaryAction {
  type: PerformanceActionType.UPDATE_EXTERNAL_SUMMARY;
  payload: PerformanceSummary;
}

export interface ResetFiltersAction {
  type: PerformanceActionType.RESET_FILTERS;
}

export type PerformanceAction =
  | SetReviewTypeAction
  | SetStudentReviewsAction
  | SetStaffReviewsAction
  | SetExternalReviewsAction
  | SetFiltersAction
  | SetSearchQueryAction
  | SetLoadingAction
  | SetErrorAction
  | SetSelectedReviewAction
  | UpdateStudentSummaryAction
  | UpdateStaffSummaryAction
  | UpdateExternalSummaryAction
  | ResetFiltersAction;

// Context Interface
export interface PerformanceContextType {
  state: PerformanceState;
  dispatch: React.Dispatch<PerformanceAction>;

  // Action Creators
  setReviewType: (type: PerformanceReviewType) => void;
  setFilters: (filters: Partial<PerformanceFilters>) => void;
  setSearchQuery: (query: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSelectedReview: (review: BasePerformanceReview | null) => void;
  resetFilters: () => void;

  // Data fetching methods
  fetchStudentReviews: () => Promise<void>;
  fetchStaffReviews: () => Promise<void>;
  fetchExternalReviews: () => Promise<void>;

  // Utility methods
  getFilteredReviews: () => BasePerformanceReview[];
  getCurrentSummary: () => PerformanceSummary;
  exportReviews: (format: 'pdf' | 'excel') => Promise<void>;
}

// Component Props Interfaces
export interface PerformanceViewProps {
  // Any props needed for the main view
}

export interface StudentPerformanceViewProps {
  reviews: StudentPerformanceReview[];
  summary: PerformanceSummary;
  onReviewSelect: (review: StudentPerformanceReview) => void;
}

export interface StaffPerformanceViewProps {
  reviews: StaffPerformanceReview[];
  summary: PerformanceSummary;
  onReviewSelect: (review: StaffPerformanceReview) => void;
}

export interface ExternalPerformanceViewProps {
  reviews: ExternalPerformanceReview[];
  summary: PerformanceSummary;
  onReviewSelect: (review: ExternalPerformanceReview) => void;
}
